'use client';

import { useState, useEffect } from 'react';
import { articles as staticArticles } from './articlesData';
import { LEGACY_DRAFTS_KEY, fetchPublishedArticles } from '@/lib/articles';
import type { ArticleBlock } from '../admin-dashboard/components/ArticleEditor';

export interface PublishedArticle {
  id: string;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  readTime: string;
  date: string;
  content: string;
  blocks?: import('../admin-dashboard/components/ArticleEditor').ArticleBlock[];
  isFromEditor?: boolean;
  // static article extras (optional)
  categoryColor?: string;
  categoryBg?: string;
  gradientColors?: string;
}

/** One shape out, whichever source the article came from. */
function toPublished(record: {
  id?: string;
  slug: string;
  title: string;
  excerpt?: string;
  category?: string;
  readTime?: string;
  read_time?: string;
  blocks?: ArticleBlock[];
  updatedAt?: string;
  updated_at?: string;
  createdAt?: string;
  created_at?: string;
}): PublishedArticle {
  const stamp =
    record.updatedAt || record.updated_at || record.createdAt || record.created_at || '';
  const blocks = (record.blocks ?? []) as ArticleBlock[];
  return {
    id: record.id || record.slug,
    slug: record.slug,
    category: record.category || 'General',
    title: record.title,
    excerpt: record.excerpt || '',
    readTime: record.readTime || record.read_time || '5 min read',
    date: stamp
      ? new Date(stamp).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })
      : '',
    content: blocksToMarkdown(blocks),
    blocks,
    isFromEditor: true,
  };
}

function blocksToMarkdown(
  blocks: import('../admin-dashboard/components/ArticleEditor').ArticleBlock[]
): string {
  return blocks
    .map((b) => {
      if (b.type === 'paragraph') return b.content;
      if (b.type === 'h2') return `## ${b.content}`;
      if (b.type === 'h3') return `### ${b.content}`;
      if (b.type === 'quote') return `> ${b.content}`;
      if (b.type === 'code') return `\`\`\`\n${b.content}\n\`\`\``;
      if (b.type === 'list') return (b.listItems || []).map((li) => `- ${li}`).join('\n');
      if (b.type === 'divider') return '---';
      if (b.type === 'callout') return `> **${b.content}**`;
      if (b.type === 'numbered-list')
        return (b.listItems || []).map((li, i) => `${i + 1}. ${li}`).join('\n');
      if (b.type === 'image') return b.imageUrl ? `![${b.imageAlt || ''}](${b.imageUrl})` : '';
      return b.content || '';
    })
    .filter(Boolean)
    .join('\n\n');
}

export function usePublishedArticles(): PublishedArticle[] {
  const [editorArticles, setEditorArticles] = useState<PublishedArticle[]>([]);

  // Published articles come from Supabase, so an article written on one machine
  // is visible everywhere and survives a cleared cache. The old localStorage
  // drafts are still read as a fallback, which keeps anything not yet migrated
  // on the site instead of vanishing the moment this shipped.
  useEffect(() => {
    let cancelled = false;

    const fromLegacyStorage = (): PublishedArticle[] => {
      try {
        const stored = localStorage.getItem(LEGACY_DRAFTS_KEY);
        if (!stored) return [];
        const drafts = JSON.parse(stored) as Array<Record<string, unknown>>;
        return drafts
          .filter((d) => d.status === 'published' && d.title && d.slug)
          .map((d) => toPublished(d as never));
      } catch {
        return [];
      }
    };

    (async () => {
      const stored = await fetchPublishedArticles();
      if (cancelled) return;
      setEditorArticles(
        stored.length ? stored.map((a) => toPublished(a as never)) : fromLegacyStorage()
      );
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Merge: editor articles first (newest), then static articles
  // Deduplicate by slug — editor articles override static ones with same slug
  const staticSlugs = new Set(editorArticles.map((a) => a.slug));
  const filteredStatic = (staticArticles as PublishedArticle[]).filter(
    (a) => !staticSlugs.has(a.slug)
  );
  return [...editorArticles, ...filteredStatic];
}

export function useArticleBySlug(slug: string): PublishedArticle | undefined {
  const all = usePublishedArticles();
  return all.find((a) => a.slug === slug);
}
